<template>
   <v-card class="page-card">
      <notification-dialog></notification-dialog>
      <withdraw-agrochemical-form v-if="canWithdraw"></withdraw-agrochemical-form>
      <apply-agrochemical-form v-if="canApply"></apply-agrochemical-form>
      <v-toolbar color="primary" elevation="0" class="page-toolbar agrochemical-toolbar">
        <v-toolbar-title>Agroquimicos</v-toolbar-title>
        <v-btn v-if="canCreate" color="white" variant="flat" class="toolbar-add-button" @click="addAgrochemical()">
          <v-icon start>add</v-icon>
          Nuevo
        </v-btn>
        <v-btn v-if="canApply" color="on-primary" variant="text" :disabled="selected.length === 0" @click="applyAgrochemical()">
          <v-icon start>terrain</v-icon>
          Aplicar
        </v-btn>
        <v-btn v-if="canWithdraw" color="on-primary" variant="text" :disabled="selected.length === 0" @click="withdraw()">
          <v-icon start>logout</v-icon>
          Retirar
        </v-btn>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-inner-icon="search"
          label="Buscar..."
          single-line
          hide-details
          variant="solo-filled"
          density="comfortable"
          max-width="320"
        />
      </v-toolbar>
      <div class="table-wrapper">
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="agrochemicals"
          :search="search"
          :items-per-page-options="CONST.ROWS_PER_PAGE_ITEMS"
          :items-per-page-text="CONST.ROWS_PER_PAGE_TEXT"
          item-value="id"
          return-object
          :show-select="canApply || canWithdraw">
          <template #[`item.price`]="{ item }">
            u$d {{ getRawItem(item).price || 0 }}
          </template>
          <template #[`item.agrochemicalType.name`]="{ item }">
            {{ getRawItem(item).agrochemicalType?.name || '' }}
          </template>
          <template #[`item.amount`]="{ item }">
            {{ getRawItem(item).amount }} {{ getMeasurmentSymbol(getRawItem(item).measurementUnit) }}
          </template>
          <template #[`item.principeActiveConcentration`]="{ item }">
            {{ getRawItem(item).principeActiveConcentration || 0 }} %
          </template>
          <template #[`item.purchaseDate`]="{ item }">
            {{ formatLocalDate(getRawItem(item).purchaseDate) }}
          </template>
          <template #[`item.expirationDate`]="{ item }">
            {{ formatLocalDate(getRawItem(item).expirationDate) }}
          </template>
          <template #[`item.actions`]="{ item }">
            <div class="action-cell">
              <v-btn v-if="canUpdate" icon size="small" variant="text" class="table-action-button" aria-label="Editar" @click="editItem(getRawItem(item))">
                <v-icon>edit</v-icon>
              </v-btn>
              <v-btn v-if="canDelete" icon size="small" variant="text" class="table-action-button table-action-danger" aria-label="Eliminar" @click="deleteItem(getRawItem(item))">
                <v-icon>delete</v-icon>
              </v-btn>
            </div>
          </template>
          <template v-slot:no-data>
            <span>{{ CONST.NO_RESULTS_CHECK_FILTERS }}</span>
            <v-btn color="primary" class="ml-3" @click="initialize">{{ CONST.REFRESH }}</v-btn>
          </template>
        </v-data-table>
      </div>
  </v-card>
</template>
<script>
import CONST from '../../services/common/Constants.js'
import { mapGetters } from 'vuex'
import WithdrawAgrochemicalForm from './WithdrawAgrochemicalForm'
import ApplyAgrochemicalsForm from './ApplyAgrochemicalsForm'
import NotificationDialog from '../Common/NotificationDialog'
import { formatLocalDate as formatDate, toLocalDateInput } from '../../services/common/UtilsFunctions'

export default {
  name: 'AgrochemicalTable',
  components: {
    'withdraw-agrochemical-form': WithdrawAgrochemicalForm,
    'apply-agrochemical-form': ApplyAgrochemicalsForm,
    'notification-dialog': NotificationDialog
  },
  data: () => ({
    CONST: {},
    selected: [],
    search: '',
    headers: [
      {title: 'Nombre', align: 'center', key: 'name'},
      {title: 'Precio Unitario', align: 'center', key: 'price'},
      {title: 'Tipo', align: 'center', key: 'agrochemicalType.name'},
      {title: 'Cantidad', align: 'center', key: 'amount'},
      {title: 'Principio Activo', align: 'center', key: 'activePrincipie', sortable: false},
      {title: 'Concentracion de P.A.', align: 'center', key: 'principeActiveConcentration', sortable: false},
      {title: 'Compra', align: 'center', key: 'purchaseDate', sortable: false},
      {title: 'Vencimiento', align: 'center', key: 'expirationDate', sortable: false},
      {title: 'Acciones', align: 'center', key: 'actions', sortable: false}
    ],
    agrochemicals: [],
    measurmentUnits: []
  }),

  computed: {
    ...mapGetters(['isRefreshTableRequired']),
    ...mapGetters('authModule', ['hasPermission']),
    canCreate () { return this.hasPermission('agrochemicals.products.create') },
    canUpdate () { return this.hasPermission('agrochemicals.products.update') },
    canDelete () { return this.hasPermission('agrochemicals.products.delete') },
    canApply () { return this.hasPermission('agrochemicals.applications.create') },
    canWithdraw () { return this.hasPermission('agrochemicals.withdrawals.create') }
  },

  created () {
    this.CONST = CONST
    this.measurmentUnits = this.$store.getters['typesModule/getMeasurmentUnits']
    this.initialize()
  },

  watch: {
    'isRefreshTableRequired': function (newValue, oldValue) {
      if (newValue === true && oldValue === false) {
        this.initialize()
        this.selected = []
      }
    }
  },

  methods: {
    async initialize () {
      await this.$store.dispatch('agrochemicalModule/getAgrochemicals')
      this.agrochemicals = this.$store.getters['agrochemicalModule/getAgrochemicals']
    },

    getRawItem (item) {
      return item.raw || item
    },

    getMeasurmentSymbol (value) {
      let measurmentUnit = this.measurmentUnits.find(unit => unit.id === value)
      return measurmentUnit ? measurmentUnit.symbol : ''
    },

    formatLocalDate (value) {
      return formatDate(value)
    },

    deleteItem (item) {
      confirm(`Estas seguro de querer eliminar: ${item.name}?`) && this.$store.dispatch('agrochemicalModule/deleteAgrochemical', item.id).then(result => {
        this.initialize()
      }).catch(error => console.log('There was an error trying to delete Agrochemical.' + error))
    },

    editItem (item) {
      item.purchaseDate = toLocalDateInput(item.purchaseDate)
      item.expirationDate = toLocalDateInput(item.expirationDate)
      this.$store.dispatch('agrochemicalModule/setSelectedItem', item)
      this.$store.dispatch('agrochemicalModule/setDialogActived', true)
    },

    addAgrochemical () {
      this.$store.dispatch('agrochemicalModule/setDialogActived', true)
    },

    withdraw () {
      let items = []
      for (const item of this.selected) {
        items.push({id: item.id, name: item.name, amount: item.amount, withdrawAmt: null})
      }
      this.$store.dispatch('agrochemicalModule/setItemsToWithdraw', items)
      this.$store.dispatch('agrochemicalModule/setWithdrawDialogActived', true)
    },

    applyAgrochemical () {
      let items = []
      for (const item of this.selected) {
        items.push({agrochemical: item, dosage: null})
      }
      this.$store.dispatch('agrochemicalModule/setItemsToApply', items)
      this.$store.dispatch('agrochemicalModule/setApplyAgrochemicalsDialogActived', true)
    }
  }
}
</script>
